import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { 
  Plus, 
  Edit, 
  Trash2,
  PlayCircle,
  Upload,
  Globe,
  Youtube,
  Clock,
  Eye,
  GripVertical,
  X,
  ToggleLeft,
  ToggleRight,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { modulesApi } from '@/services/api';
import { Course } from '@/types';
import { toast } from 'sonner';

const moduleSchema = z.object({
  title: z.string().min(3, 'El título debe tener al menos 3 caracteres'),
  description: z.string().optional(),
  type: z.enum(['VIDEO', 'TEXT', 'ASSIGNMENT']),
  videoType: z.enum(['LOCAL', 'YOUTUBE', 'URL']).optional(),
  videoUrl: z.string().optional(),
  youtubeId: z.string().optional(),
  videoDuration: z.number().min(1, 'La duración del video debe ser mayor a 0 segundos').optional(),
  content: z.string().optional(),
  thumbnail: z.string().optional(),
  isPreview: z.boolean().default(false),
}).refine(data => {
  // Si es un video LOCAL, debe tener duración
  if (data.type === 'VIDEO' && data.videoType === 'LOCAL') {
    return data.videoDuration && data.videoDuration > 0;
  }
  return true;
}, {
  message: 'Para videos locales, debes especificar la duración en segundos',
  path: ['videoDuration'],
});

type ModuleFormData = z.infer<typeof moduleSchema>;

interface Module {
  id: string;
  title: string;
  description?: string;
  type: 'VIDEO' | 'TEXT' | 'ASSIGNMENT';
  videoType?: 'LOCAL' | 'YOUTUBE' | 'URL';
  videoUrl?: string;
  youtubeId?: string;
  videoFile?: string;
  videoDuration?: number;
  content?: string;
  thumbnail?: string;
  order: number;
  isPreview: boolean;
  isactive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ModulesManagerProps {
  course: Course;
  isOpen: boolean;
  onClose: () => void;
}

export const ModulesManager: React.FC<ModulesManagerProps> = ({
  course,
  isOpen,
  onClose
}) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    module: Module | null;
    isDeleting: boolean;
  }>({
    isOpen: false,
    module: null,
    isDeleting: false
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ModuleFormData>({
    resolver: zodResolver(moduleSchema),
  });

  const watchVideoType = watch('videoType');
  const watchType = watch('type');

  useEffect(() => {
    if (isOpen) {
      fetchModules();
    }
  }, [isOpen]);

  const fetchModules = async () => {
    try {
      setIsLoading(true);
      const response = await modulesApi.getByCourse(course.id);
      if (response.data.success) {
        setModules(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Error al cargar los módulos');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: ModuleFormData) => {
    try {
      // Validación: Si es un video LOCAL, debe tener duración
      if (data.type === 'VIDEO' && data.videoType === 'LOCAL' && (!data.videoDuration || data.videoDuration <= 0)) {
        toast.error('Para videos locales, debes especificar la duración en segundos');
        return;
      }

      const formData = new FormData();

      // Agregar datos del módulo
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Special handling for videoDuration to ensure it's sent correctly
          if (key === 'videoDuration') {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Agregar archivo si existe
      if (selectedFile && data.videoType === 'LOCAL') {
        formData.append('video', selectedFile);
      }

      if (editingModule) {
        formData.append('courseId', course.id);
        await modulesApi.update(editingModule.id, formData);
        toast.success('Módulo actualizado exitosamente');
      } else {
        await modulesApi.create(course.id, formData);
        toast.success('Módulo creado exitosamente');
      }

      setIsModuleModalOpen(false);
      setEditingModule(null);
      setSelectedFile(null);
      reset();
      await fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar el módulo');
    }
  };

  const handleEdit = (module: Module) => {
    setEditingModule(module);
    reset({
      title: module.title,
      description: module.description || '',
      type: module.type,
      videoType: module.videoType,
      videoUrl: module.videoUrl || '',
      youtubeId: module.youtubeId || '',
      videoDuration: module.videoDuration,
      content: module.content || '',
      thumbnail: module.thumbnail || '',
      isPreview: module.isPreview,
    });
    setIsModuleModalOpen(true);
  };

  const handleDeleteClick = (module: Module) => {
    setDeleteModal({
      isOpen: true,
      module,
      isDeleting: false
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.module) return;
    
    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      await modulesApi.delete(deleteModal.module.id);
      toast.success('Módulo eliminado exitosamente');
      setDeleteModal({
        isOpen: false,
        module: null,
        isDeleting: false
      });
      await fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar el módulo');
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      module: null,
      isDeleting: false
    });
  };

  const handleToggleStatus = async (module: Module) => {
    try {
      await modulesApi.toggleStatus(module.id);
      toast.success(`Módulo ${module.isactive ? 'desactivado' : 'activado'} exitosamente`);
      await fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar estado del módulo');
    }
  };

  const handleMoveUp = async (currentIndex: number) => {
    if (currentIndex === 0) return;
    
    try {
      const newModulesOrder = [...modules];
      [newModulesOrder[currentIndex], newModulesOrder[currentIndex - 1]] = 
        [newModulesOrder[currentIndex - 1], newModulesOrder[currentIndex]];
      
      const moduleIds = newModulesOrder.map(m => m.id);
      await modulesApi.reorder(course.id, moduleIds);
      toast.success('Orden actualizado exitosamente');
      await fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al reordenar módulos');
    }
  };

  const handleMoveDown = async (currentIndex: number) => {
    if (currentIndex === modules.length - 1) return;
    
    try {
      const newModulesOrder = [...modules];
      [newModulesOrder[currentIndex], newModulesOrder[currentIndex + 1]] = 
        [newModulesOrder[currentIndex + 1], newModulesOrder[currentIndex]];
      
      const moduleIds = newModulesOrder.map(m => m.id);
      await modulesApi.reorder(course.id, moduleIds);
      toast.success('Orden actualizado exitosamente');
      await fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al reordenar módulos');
    }
  };

  const openCreateModal = () => {
    setEditingModule(null);
    setSelectedFile(null);
    reset({
      type: 'VIDEO',
      videoType: 'LOCAL',
      isPreview: false,
    });
    setIsModuleModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 500 * 1024 * 1024) { // 500MB
        toast.error('El archivo es demasiado grande. Máximo 500MB.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const getVideoTypeIcon = (videoType?: string) => {
    switch (videoType) {
      case 'LOCAL': return <Upload className="h-4 w-4" />;
      case 'YOUTUBE': return <Youtube className="h-4 w-4" />;
      case 'URL': return <Globe className="h-4 w-4" />;
      default: return <PlayCircle className="h-4 w-4" />;
    }
  };

  const getVideoTypeLabel = (videoType?: string) => {
    switch (videoType) {
      case 'LOCAL': return 'Archivo Local';
      case 'YOUTUBE': return 'YouTube';
      case 'URL': return 'URL Externa';
      default: return 'Video';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Gestionar Módulos - {course.title}
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                {modules.length} módulos en total
              </p>
            </div>
            <div className="flex space-x-2">
              <Button onClick={openCreateModal} leftIcon={Plus} size="sm">
                Nuevo Módulo
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Modules List */}
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-20 bg-gray-200 rounded"></div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {modules.length === 0 ? (
                <Card className="text-center py-12">
                  <PlayCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No hay módulos creados
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Comienza creando tu primer módulo para este curso
                  </p>
                  <Button onClick={openCreateModal} leftIcon={Plus}>
                    Crear Módulo
                  </Button>
                </Card>
              ) : (
                modules.map((module, index) => (
                  <Card key={module.id}>
                    <div className="flex items-start space-x-4">
                      {/* Drag Handle */}
                      <div className="pt-2">
                        <GripVertical className="h-5 w-5 text-gray-400" />
                      </div>

                      {/* Module Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-mono text-gray-500">
                                #{(index + 1).toString().padStart(2, '0')}
                              </span>
                              <h3 className="font-semibold text-gray-900">
                                {module.title}
                              </h3>
                              <span className={`px-2 py-1 text-xs rounded font-medium ${
                                module.isactive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {module.isactive ? 'Activo' : 'Inactivo'}
                              </span>
                              {module.isPreview && (
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                  Vista previa
                                </span>
                              )}
                            </div>
                            {module.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {module.description}
                              </p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                              <div className="flex items-center">
                                {getVideoTypeIcon(module.videoType)}
                                <span className="ml-1">
                                  {getVideoTypeLabel(module.videoType)}
                                </span>
                              </div>
                              {module.videoDuration && (
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {Math.floor(module.videoDuration / 60)}:
                                  {(module.videoDuration % 60).toString().padStart(2, '0')}
                                </div>
                              )}
                              <span className="text-xs text-gray-500">
                                Tipo: {module.type}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-1">
                        {/* Order buttons */}
                        <div className="flex flex-col space-y-1 mr-3">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            title="Mover hacia arriba"
                            className={`h-9 w-9 p-2 rounded-md border-2 transition-all duration-200 ${
                              index === 0 
                                ? 'border-gray-200 bg-gray-50 opacity-40 cursor-not-allowed' 
                                : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50 shadow-sm hover:shadow-md'
                            }`}
                          >
                            <ChevronUp className={`h-5 w-5 ${index === 0 ? 'text-gray-400' : 'text-slate-600'}`} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleMoveDown(index)}
                            disabled={index === modules.length - 1}
                            title="Mover hacia abajo"
                            className={`h-9 w-9 p-2 rounded-md border-2 transition-all duration-200 ${
                              index === modules.length - 1 
                                ? 'border-gray-200 bg-gray-50 opacity-40 cursor-not-allowed' 
                                : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50 shadow-sm hover:shadow-md'
                            }`}
                          >
                            <ChevronDown className={`h-5 w-5 ${index === modules.length - 1 ? 'text-gray-400' : 'text-slate-600'}`} />
                          </Button>
                        </div>

                        <Button variant="ghost" size="sm" title="Ver módulo">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEdit(module)} 
                          title="Editar módulo"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleToggleStatus(module)} 
                          title={module.isactive ? "Desactivar módulo" : "Activar módulo"}
                        >
                          {module.isactive ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={() => handleDeleteClick(module)} 
                          title="Eliminar módulo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Module Modal */}
      {isModuleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-60">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingModule ? 'Editar Módulo' : 'Crear Nuevo Módulo'}
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  {...register('title')}
                  label="Título del módulo"
                  placeholder="Ej: Introducción a la automatización"
                  error={errors.title?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción (opcional)
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe el contenido del módulo..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de módulo
                  </label>
                  <select
                    {...register('type')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="VIDEO">Video</option>
                    <option value="TEXT">Texto</option>
                    <option value="ASSIGNMENT">Tarea</option>
                  </select>
                </div>

                {watchType === 'VIDEO' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de video
                      </label>
                      <select
                        {...register('videoType')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="URL">URL Externa</option>
                        <option value="YOUTUBE">YouTube</option>
                        <option value="LOCAL">Archivo Local</option>
                      </select>
                    </div>

                    {watchVideoType === 'LOCAL' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Archivo de video
                        </label>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleFileChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {selectedFile && (
                          <p className="text-sm text-gray-600 mt-1">
                            Archivo seleccionado: {selectedFile.name}
                          </p>
                        )}
                      </div>
                    )}

                    {(watchVideoType === 'URL' || watchVideoType === 'YOUTUBE') && (
                      <Input
                        {...register('videoUrl')}
                        label="URL del video"
                        placeholder={
                          watchVideoType === 'YOUTUBE'
                            ? "https://www.youtube.com/watch?v=..."
                            : "https://ejemplo.com/video.mp4"
                        }
                        error={errors.videoUrl?.message}
                      />
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción del video (instrucciones, links, etc.)
                      </label>
                      <textarea
                        {...register('content')}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Aquí puedes agregar instrucciones específicas, links útiles, notas importantes para este video..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Esta información se mostrará junto al video para ayudar a los estudiantes
                      </p>
                    </div>

                    <Input
                      {...register('videoDuration', { valueAsNumber: true })}
                      type="number"
                      min="0"
                      label="Duración (segundos)"
                      placeholder="300"
                    />
                  </>
                )}

                {watchType === 'TEXT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contenido del módulo
                    </label>
                    <textarea
                      {...register('content')}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Escribe el contenido del módulo aquí..."
                    />
                  </div>
                )}

                <Input
                  {...register('thumbnail')}
                  type="url"
                  label="URL de imagen (Thumbnail)"
                  placeholder="https://ejemplo.com/imagen.jpg"
                />

                <div className="flex items-center">
                  <input
                    {...register('isPreview')}
                    type="checkbox"
                    id="isPreview"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPreview" className="ml-2 block text-sm text-gray-900">
                    Permitir vista previa (accesible sin inscripción)
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => {
                      setIsModuleModalOpen(false);
                      setSelectedFile(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    isLoading={isSubmitting}
                  >
                    {editingModule ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Módulo"
        message={`¿Estás seguro de eliminar el módulo "${deleteModal.module?.title}"? Esta acción no se puede deshacer y eliminará todo el progreso de los estudiantes en este módulo.`}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteModal.isDeleting}
      />
    </div>
  );
};